import { useEffect, useRef, useState } from "react";
import { Check, Copy } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

import type { LabJob, LabMediaReference } from "./api";
import { resolveLabProviderIdentity } from "./providerIdentity";

const settingValue = (value: unknown, fallback = "Not set") => {
  if (value == null || value === "") return fallback;
  if (typeof value === "boolean") return value ? "On" : "Off";
  return String(value);
};

const mediaUrl = (media: LabMediaReference | null | undefined) => media?.url;

export function LabRunRecipe({
  job,
  onUseSettings,
}: {
  job: LabJob;
  onUseSettings: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const copiedTimer = useRef<number | null>(null);
  const request = job.request;
  const providerIdentity = resolveLabProviderIdentity(job);

  useEffect(
    () => () => {
      if (copiedTimer.current != null) {
        window.clearTimeout(copiedTimer.current);
      }
    },
    [],
  );

  if (!request) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Generation recipe</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            The immutable request manifest is unavailable for this legacy run.
          </p>
        </CardContent>
      </Card>
    );
  }

  const ltxOptions =
    request.provider_options?.provider_id === "ltx_comfy"
      ? request.provider_options
      : null;

  const inputMedia = [
    {
      label: "First frame",
      media: request.inputs.first_frame ?? job.inputs.first_frame,
    },
    {
      label: "Last frame",
      media: request.inputs.last_frame ?? job.inputs.last_frame,
    },
    ...request.inputs.reference_images.map((media, index) => ({
      label: `Reference ${index + 1}`,
      media,
    })),
  ].filter((entry): entry is { label: string; media: LabMediaReference } =>
    Boolean(entry.media),
  );

  const copyPrompt = async () => {
    try {
      await navigator.clipboard.writeText(request.prompt);
      setCopied(true);
      if (copiedTimer.current != null) {
        window.clearTimeout(copiedTimer.current);
      }
      copiedTimer.current = window.setTimeout(() => setCopied(false), 1500);
    } catch {
      setCopied(false);
    }
  };

  return (
    <Card>
      <CardHeader className="gap-3 pb-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <CardTitle className="text-sm">Generation recipe</CardTitle>
          <p className="mt-1 text-xs text-muted-foreground">
            Exact prompt and settings saved with this run.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={() => void copyPrompt()}>
            {copied ? (
              <Check className="size-4" />
            ) : (
              <Copy className="size-4" />
            )}
            {copied ? "Copied" : "Copy prompt"}
          </Button>
          <Button variant="outline" size="sm" onClick={onUseSettings}>
            Use prompt &amp; settings
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="space-y-2">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Prompt
          </p>
          <p className="select-text whitespace-pre-wrap rounded-lg border bg-muted/30 p-3 text-sm leading-relaxed">
            {request.prompt}
          </p>
        </div>
        {request.negative_prompt ? (
          <div className="space-y-2">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Negative prompt
            </p>
            <p className="select-text whitespace-pre-wrap rounded-lg border bg-muted/30 p-3 text-sm leading-relaxed">
              {request.negative_prompt}
            </p>
          </div>
        ) : null}
        <dl className="grid gap-3 text-xs sm:grid-cols-2 xl:grid-cols-4">
          <div>
            <dt className="text-muted-foreground">Provider identity</dt>
            <dd className="mt-1">{providerIdentity.label}</dd>
          </div>
          {providerIdentity.provenance !== "recorded" ? (
            <div>
              <dt className="text-muted-foreground">Original route</dt>
              <dd className="mt-1 font-mono">
                {providerIdentity.originalProviderId}
              </dd>
            </div>
          ) : null}
          {request.provider_options?.provider_id ? (
            <div>
              <dt className="text-muted-foreground">Exact route</dt>
              <dd className="mt-1 font-mono">
                {request.provider_options.provider_id}
              </dd>
            </div>
          ) : null}
          <div>
            <dt className="text-muted-foreground">Model</dt>
            <dd className="mt-1 break-all font-mono">
              {settingValue(request.model_id, "Deployed default")}
            </dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Duration</dt>
            <dd className="mt-1">{request.duration_seconds}s</dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Aspect ratio</dt>
            <dd className="mt-1">
              {settingValue(request.aspect_ratio, "Native")}
            </dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Resolution</dt>
            <dd className="mt-1">
              {settingValue(request.resolution, "Provider default")}
            </dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Native audio</dt>
            <dd className="mt-1">{settingValue(request.generate_audio)}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Seed</dt>
            <dd className="mt-1">{settingValue(request.seed, "Random")}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Registry digest</dt>
            <dd
              className="mt-1 truncate font-mono"
              title={request.registry_digest ?? undefined}
            >
              {request.registry_digest
                ? request.registry_digest.slice(0, 12)
                : "Not recorded"}
            </dd>
          </div>
          {ltxOptions?.workflow_id ? (
            <div>
              <dt className="text-muted-foreground">Workflow</dt>
              <dd className="mt-1 font-mono">{ltxOptions.workflow_id}</dd>
            </div>
          ) : null}
          {ltxOptions?.lora_id ? (
            <div>
              <dt className="text-muted-foreground">LoRA</dt>
              <dd className="mt-1 font-mono">{ltxOptions.lora_id}</dd>
            </div>
          ) : null}
          {ltxOptions?.lora_strength != null ? (
            <div>
              <dt className="text-muted-foreground">LoRA strength</dt>
              <dd className="mt-1">{ltxOptions.lora_strength}</dd>
            </div>
          ) : null}
        </dl>
        {job.attempt ? (
          <div className="space-y-2">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Provider attempt
            </p>
            <dl className="grid gap-3 rounded-lg border bg-muted/30 p-3 text-xs sm:grid-cols-2 xl:grid-cols-4">
              <div>
                <dt className="text-muted-foreground">State</dt>
                <dd className="mt-1 font-medium">{job.attempt.state}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Concrete provider</dt>
                <dd className="mt-1 font-mono">
                  {settingValue(job.attempt.concrete_provider_id)}
                </dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Task</dt>
                <dd className="mt-1 break-all font-mono">
                  {settingValue(job.attempt.task_id, "Not accepted")}
                </dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Endpoint / API</dt>
                <dd className="mt-1 font-mono">
                  {[job.attempt.endpoint_kind, job.attempt.api_version]
                    .filter(Boolean)
                    .join(" / ") || "Not recorded"}
                </dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Attempt history</dt>
                <dd className="mt-1">
                  {job.attempt.attempt_history?.length ?? 0} recorded event(s)
                </dd>
              </div>
              {job.attempt.error_code ? (
                <div>
                  <dt className="text-muted-foreground">Attempt error</dt>
                  <dd className="mt-1 font-mono">{job.attempt.error_code}</dd>
                </div>
              ) : null}
            </dl>
          </div>
        ) : null}
        {job.result?.usage_ledger ? (
          <div className="space-y-2">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Usage and cost
            </p>
            <dl className="grid gap-3 rounded-lg border bg-muted/30 p-3 text-xs sm:grid-cols-2 xl:grid-cols-4">
              <div>
                <dt className="text-muted-foreground">Pricing identity</dt>
                <dd className="mt-1 font-mono">
                  {settingValue(job.result.usage_ledger.pricing_key)} /{" "}
                  {settingValue(job.result.usage_ledger.pricing_version)}
                </dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Estimate</dt>
                <dd className="mt-1">
                  {job.result.usage_ledger.estimated_cost_usd != null
                    ? `$${job.result.usage_ledger.estimated_cost_usd}`
                    : "Unknown"}
                </dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Provider reported</dt>
                <dd className="mt-1">
                  {job.result.usage_ledger.provider_reported_cost_usd != null
                    ? `$${job.result.usage_ledger.provider_reported_cost_usd}`
                    : "Not reported"}
                </dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Reconciliation</dt>
                <dd className="mt-1">
                  {job.result.usage_ledger.reconciliation_state.replaceAll(
                    "_",
                    " ",
                  )}
                </dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Possible billing</dt>
                <dd className="mt-1">
                  {job.result.usage_ledger.possible_billing ? "Yes" : "No"}
                </dd>
              </div>
              {job.result.usage_ledger.variance_reason ? (
                <div>
                  <dt className="text-muted-foreground">Variance reason</dt>
                  <dd className="mt-1">
                    {job.result.usage_ledger.variance_reason.replaceAll(
                      "_",
                      " ",
                    )}
                  </dd>
                </div>
              ) : null}
            </dl>
          </div>
        ) : job.result?.usage ? (
          <div className="space-y-2">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Usage and cost
            </p>
            <pre className="overflow-x-auto rounded-lg border bg-muted/30 p-3 text-xs">
              {JSON.stringify(job.result.usage, null, 2)}
            </pre>
          </div>
        ) : null}
        {inputMedia.length ? (
          <div className="space-y-2">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Inputs
            </p>
            <div className="flex flex-wrap gap-3">
              {inputMedia.map(({ label, media }) => (
                <div key={`${label}-${media.key}`} className="w-24 space-y-1.5">
                  <div className="grid aspect-square place-items-center overflow-hidden rounded-md border bg-muted">
                    {mediaUrl(media) ? (
                      <img
                        src={mediaUrl(media)}
                        alt={`${label} used for this run`}
                        className="size-full object-cover"
                      />
                    ) : (
                      <span className="px-2 text-center text-[10px] text-muted-foreground">
                        Stored input
                      </span>
                    )}
                  </div>
                  <Badge
                    variant="outline"
                    className="max-w-full truncate text-[10px]"
                  >
                    {label}
                  </Badge>
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
